import { useCallback } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { API_ORIGIN } from "@/constants"

export interface OnboardingStep {
  id: string
  title: string
  description: string
  component: string
  required?: boolean
  skippable?: boolean
  enabled?: boolean
  step_type?: string
  validation_config?: Record<string, any>
  validation_message?: string
}

export interface OnboardingStatus {
  group_name: string
  has_onboarding: boolean
  is_completed: boolean
  current_step_index: number
  completed_step_ids: string[]
  steps: OnboardingStep[]
  total_steps: number
  progress_percent: number
  completed_at: string | null
}

export const ONBOARDING_STATUS_QUERY_KEY = ["onboarding", "status"] as const

async function fetchOnboardingStatus(revalidate = false): Promise<OnboardingStatus | null> {
  const token = localStorage.getItem("auth_token")
  if (!token) {
    return null
  }

  const params = new URLSearchParams()
  if (revalidate) {
    params.append("revalidate", "true")
  }

  const response = await fetch(
    `${API_ORIGIN}/api/accounts/onboarding/status?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  )

  if (response.status === 401) {
    return null
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || "Failed to fetch onboarding status")
  }

  return response.json()
}

function statusFromCompletePayload(data: Record<string, unknown>): OnboardingStatus {
  return {
    group_name: String(data.group_name ?? ""),
    has_onboarding: Boolean(data.has_onboarding ?? true),
    is_completed: Boolean(data.is_completed ?? data.is_flow_completed ?? false),
    current_step_index: Number(data.current_step_index ?? 0),
    completed_step_ids: (data.completed_step_ids as string[]) ?? [],
    steps: (data.steps as OnboardingStep[]) ?? [],
    total_steps: Number(data.total_steps ?? 0),
    progress_percent: Number(data.progress_percent ?? 0),
    completed_at: (data.completed_at as string | null) ?? null,
  }
}

export function useOnboarding() {
  const queryClient = useQueryClient()

  const statusQuery = useQuery({
    queryKey: ONBOARDING_STATUS_QUERY_KEY,
    queryFn: () => fetchOnboardingStatus(true),
    staleTime: 30_000,
  })

  const completeMutation = useMutation({
    mutationFn: async (stepId: string) => {
      const token = localStorage.getItem("auth_token")
      if (!token) {
        throw new Error("Not authenticated")
      }

      const response = await fetch(
        `${API_ORIGIN}/api/accounts/onboarding/step/${stepId}/complete`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      )

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        return { success: false as const, error: data.error || "Failed to complete step" }
      }

      const nextStatus = statusFromCompletePayload(data)
      queryClient.setQueryData(ONBOARDING_STATUS_QUERY_KEY, nextStatus)
      return { success: true as const, data }
    },
  })

  const fetchStatus = useCallback(
    async (options?: { revalidate?: boolean; silent?: boolean }) => {
      if (options?.revalidate) {
        await queryClient.invalidateQueries({ queryKey: ONBOARDING_STATUS_QUERY_KEY })
      } else {
        await queryClient.refetchQueries({ queryKey: ONBOARDING_STATUS_QUERY_KEY })
      }
    },
    [queryClient],
  )

  const completeStep = useCallback(
    async (stepId: string) => {
      try {
        return await completeMutation.mutateAsync(stepId)
      } catch {
        return { success: false as const, error: "Network error" }
      }
    },
    [completeMutation],
  )

  return {
    status: statusQuery.data ?? null,
    isLoading: statusQuery.isLoading,
    error: statusQuery.error ? String(statusQuery.error) : null,
    fetchStatus,
    completeStep,
  }
}
